/**
 * AI/OCR consent, license verification and OCR→invoice strings (EN / TR / AR).
 * Consent copy mirrors the mobile app so both platforms show identical wording.
 */
export const consentLocales = {
  en: {
    ai: {
      consent: {
      title: 'AI & OCR Data Processing',
      allow: 'Allow',
      notNow: 'Not Now',
      details: 'View Details',
      toggleTitle: 'Enable AI/OCR features',
      toggleDesc: 'Requires your permission before processing.',
      fullMessage:
        'Before using AI/OCR features, we need your permission.\n\nWhat data may be sent:\n• Content of files you upload (for OCR/analysis)\n• Text extracted from files (OCR text)\n• File metadata (file name, type, size)\n• Your AI assistant prompts and messages\n\nWho receives it:\n• FutureDocs backend services\n• Google Cloud Platform / Firebase (hosting and processing)\n\nYou can withdraw permission anytime in Settings.',
      disableTitle: 'Disable AI/OCR features?',
      disableMessage:
        'This will stop AI/OCR processing for new actions. Previously processed results may remain available in your account until you delete the related content.',
      disableConfirm: 'Disable',
      whatWeSendTitle: 'What data is sent',
      whatWeSendBody:
        'When enabled, AI/OCR features may process: the content of uploaded documents (or selected parts), extracted OCR text, file metadata, and the messages you type into the AI assistant.',
      whoTitle: 'Who receives the data',
      whoBody:
        'AI/OCR features are provided through FutureDocs services hosted on Google Cloud Platform, including Google Firebase. Your data is transmitted over TLS.',
      controlTitle: 'Your control',
      controlBody:
        'AI/OCR processing is optional. You can enable/disable it anytime here. Disabling prevents new AI/OCR processing requests from this device.',
      note:
        'If you choose Not Now, AI assistant and OCR-based processing will be disabled until you enable it in Settings.',
      enabledBadge: 'Enabled',
      disabledBadge: 'Disabled',
      grantedOn: 'Enabled on {{date}}',
      revokedOn: 'Disabled on {{date}}',
      blockedTitle: 'AI features are turned off',
      blockedDesc: 'Enable AI/OCR data processing to use the assistant.'
    },
      ocr: {
        transferToInvoice: 'Transfer to invoice',
        transferToInvoiceDesc: 'Create an invoice pre-filled from the OCR text.'
      }
    },
    settings: {
      aiDataProcessing: 'AI & OCR Data Processing',
      aiDataProcessingDesc: 'Control AI/OCR features and consent',
      privacyRequests: 'Personal data requests',
      privacyRequestsDesc: 'File KVKK / GDPR requests and track deadlines',
      licenseVerification: 'License Verification',
      licenseVerificationDesc: 'Verify a license key and see its status'
    },
    licenseCheck: {
      intro: 'Enter a 25-character license key to check its status, owner and activation.',
      checking: 'Checking license…',
      found: 'License found',
      notFound: 'No license matches this key',
      notFoundDesc: 'Check the key for typos. If the problem continues, open a support request.',
      viewDetails: 'View details',
      checkAnother: 'Check another key',
      status: 'Status',
      activated: 'Activated',
      available: 'Available for activation',
      inactive: 'Inactive',
      type: 'License type',
      soldTo: 'Sold to',
      usedBy: 'Used by',
      activatedBy: 'Activated by',
      activatedAt: 'Activated on',
      expiresAt: 'Expires on',
      paste: 'Paste',
      clear: 'Clear',
      support: 'Open support request',
      purchase: 'Buy a license',
      history: 'Recent checks',
      noHistory: 'Your recent license checks will appear here.'
    }
  },
  tr: {
    ai: {
      consent: {
      title: 'AI ve OCR Veri İşleme',
      allow: 'İzin Ver',
      notNow: 'Şimdi Değil',
      details: 'Ayrıntıları Gör',
      toggleTitle: 'AI/OCR özelliklerini etkinleştir',
      toggleDesc: 'İşlemeden önce izninizi gerektirir.',
      fullMessage:
        'AI/OCR özelliklerini kullanmadan önce izninize ihtiyacımız var.\n\nGönderilebilecek veriler:\n• Yüklediğiniz dosyaların içeriği (OCR/analiz için)\n• Dosyalardan çıkarılan metin (OCR metni)\n• Dosya meta verileri (dosya adı, tür, boyut)\n• AI asistan istemleriniz ve mesajlarınız\n\nKim alır:\n• FutureDocs arka uç hizmetleri\n• Google Cloud Platform / Firebase (barındırma ve işleme)\n\nİzni istediğiniz zaman Ayarlar’dan geri çekebilirsiniz.',
      disableTitle: 'AI/OCR özellikleri kapatılsın mı?',
      disableMessage:
        'Bu, yeni işlemler için AI/OCR işlemeyi durdurur. Daha önce işlenen sonuçlar, ilgili içeriği silene kadar hesabınızda kullanılabilir kalabilir.',
      disableConfirm: 'Kapat',
      whatWeSendTitle: 'Hangi veriler gönderilir',
      whatWeSendBody:
        'Etkinleştirildiğinde AI/OCR özellikleri şunları işleyebilir: yüklenen belgelerinin içeriği (veya seçilen kısımları), çıkarılan OCR metni, dosya meta verileri ve AI asistanına yazdığınız mesajlar.',
      whoTitle: 'Veriyi kim alır',
      whoBody:
        'AI/OCR özellikleri, Google Firebase dahil Google Cloud Platform üzerinde barındırılan FutureDocs hizmetleri aracılığıyla sunulur. Verileriniz TLS üzerinden iletilir.',
      controlTitle: 'Kontrolünüz',
      controlBody:
        'AI/OCR işleme isteğe bağlıdır. İstediğiniz zaman buradan açıp kapatabilirsiniz. Kapatmak, bu cihazdan yeni AI/OCR işleme isteklerini engeller.',
      note:
        'Şimdi Değil’i seçerseniz, AI asistan ve OCR tabanlı işleme Ayarlar’dan etkinleştirene kadar kapalı kalır.',
      enabledBadge: 'Açık',
      disabledBadge: 'Kapalı',
      grantedOn: '{{date}} tarihinde açıldı',
      revokedOn: '{{date}} tarihinde kapatıldı',
      blockedTitle: 'AI özellikleri kapalı',
      blockedDesc: 'Asistanı kullanmak için AI/OCR veri işlemeyi etkinleştirin.'
    },
      ocr: {
        transferToInvoice: 'Faturaya aktar',
        transferToInvoiceDesc: 'OCR metninden otomatik doldurulmuş fatura oluşturun.'
      }
    },
    settings: {
      aiDataProcessing: 'AI ve OCR Veri İşleme',
      aiDataProcessingDesc: 'AI/OCR özelliklerini ve onayı yönetin',
      privacyRequests: 'Kişisel veri başvuruları',
      privacyRequestsDesc: 'KVKK / GDPR başvurusu yapın ve süreyi takip edin',
      licenseVerification: 'Lisans Doğrulama',
      licenseVerificationDesc: 'Lisans anahtarını doğrulayın ve durumunu görün'
    },
    licenseCheck: {
      intro: 'Durumunu, sahibini ve aktivasyonunu görmek için 25 karakterlik lisans anahtarını girin.',
      checking: 'Lisans kontrol ediliyor…',
      found: 'Lisans bulundu',
      notFound: 'Bu anahtarla eşleşen lisans yok',
      notFoundDesc: 'Anahtarı yazım hatalarına karşı kontrol edin. Sorun devam ederse destek talebi oluşturun.',
      viewDetails: 'Ayrıntıları gör',
      checkAnother: 'Başka anahtar sorgula',
      status: 'Durum',
      activated: 'Aktive edildi',
      available: 'Aktivasyona hazır',
      inactive: 'Pasif',
      type: 'Lisans türü',
      soldTo: 'Satılan firma',
      usedBy: 'Kullanan firma',
      activatedBy: 'Aktive eden',
      activatedAt: 'Aktivasyon tarihi',
      expiresAt: 'Bitiş tarihi',
      paste: 'Yapıştır',
      clear: 'Temizle',
      support: 'Destek talebi oluştur',
      purchase: 'Lisans satın al',
      history: 'Son sorgular',
      noHistory: 'Son lisans sorgularınız burada görünecek.'
    }
  },
  ar: {
    ai: {
      consent: {
      title: 'معالجة بيانات الذكاء الاصطناعي وOCR',
      allow: 'موافقة',
      notNow: 'ليس الآن',
      details: 'عرض التفاصيل',
      toggleTitle: 'تفعيل ميزات الذكاء الاصطناعي / OCR',
      toggleDesc: 'يتطلب ذلك إذنك قبل المعالجة.',
      fullMessage:
        'قبل استخدام ميزات الذكاء الاصطناعي/‏OCR نحتاج إلى إذنك.\n\nما البيانات التي قد تُرسل:\n• محتوى الملفات التي ترفعها (لـ OCR/التحليل)\n• النص المستخرج من الملفات (نص OCR)\n• بيانات الملف الوصفية (الاسم، النوع، الحجم)\n• رسائلك وأسئلتك لمساعد الذكاء الاصطناعي\n\nمن يستلمها:\n• خدمات FutureDocs الخلفية\n• منصة Google Cloud / Firebase (الاستضافة والمعالجة)\n\nيمكنك سحب الإذن في أي وقت من الإعدادات.',
      disableTitle: 'إيقاف ميزات الذكاء الاصطناعي/‏OCR؟',
      disableMessage:
        'سيؤدي هذا إلى إيقاف المعالجة للعمليات الجديدة. قد تظل النتائج التي تمت معالجتها سابقاً متاحة حتى تقوم بحذف المحتوى المرتبط بها.',
      disableConfirm: 'إيقاف',
      whatWeSendTitle: 'ما البيانات التي تُرسل',
      whatWeSendBody:
        'عند التفعيل قد تتم معالجة: محتوى المستندات المرفوعة (أو أجزاء محددة)، نص OCR المستخرج، بيانات الملف الوصفية، والرسائل التي تكتبها للمساعد.',
      whoTitle: 'من يستلم البيانات',
      whoBody:
        'يتم توفير ميزات الذكاء الاصطناعي/‏OCR عبر خدمات FutureDocs المستضافة على Google Cloud Platform بما في ذلك Google Firebase. يتم نقل البيانات عبر TLS.',
      controlTitle: 'التحكم بيدك',
      controlBody:
        'معالجة الذكاء الاصطناعي/‏OCR اختيارية. يمكنك تفعيلها/إيقافها في أي وقت هنا. الإيقاف يمنع طلبات المعالجة الجديدة من هذا الجهاز.',
      note:
        'إذا اخترت "ليس الآن" سيتم تعطيل المساعد وميزات OCR/AI حتى تقوم بتفعيلها من الإعدادات.',
      enabledBadge: 'مفعّل',
      disabledBadge: 'معطّل',
      grantedOn: 'تم التفعيل في {{date}}',
      revokedOn: 'تم التعطيل في {{date}}',
      blockedTitle: 'ميزات الذكاء الاصطناعي متوقفة',
      blockedDesc: 'فعّل معالجة بيانات AI/OCR لاستخدام المساعد.'
    },
      ocr: {
        transferToInvoice: 'تحويل إلى فاتورة',
        transferToInvoiceDesc: 'أنشئ فاتورة معبأة مسبقًا من نص OCR.'
      }
    },
    settings: {
      aiDataProcessing: 'معالجة بيانات الذكاء الاصطناعي وOCR',
      aiDataProcessingDesc: 'التحكم بميزات AI/OCR والموافقة',
      privacyRequests: 'طلبات البيانات الشخصية',
      privacyRequestsDesc: 'قدّم طلبات KVKK / GDPR وتابع المهل',
      licenseVerification: 'التحقق من الترخيص',
      licenseVerificationDesc: 'تحقق من مفتاح الترخيص واعرض حالته'
    },
    licenseCheck: {
      intro: 'أدخل مفتاح الترخيص المكون من 25 حرفًا للتحقق من حالته ومالكه وتفعيله.',
      checking: 'جارٍ التحقق من الترخيص…',
      found: 'تم العثور على الترخيص',
      notFound: 'لا يوجد ترخيص يطابق هذا المفتاح',
      notFoundDesc: 'تحقق من المفتاح بحثًا عن أخطاء. إذا استمرت المشكلة، افتح طلب دعم.',
      viewDetails: 'عرض التفاصيل',
      checkAnother: 'تحقق من مفتاح آخر',
      status: 'الحالة',
      activated: 'مفعّل',
      available: 'متاح للتفعيل',
      inactive: 'غير نشط',
      type: 'نوع الترخيص',
      soldTo: 'مباع إلى',
      usedBy: 'مستخدم من قبل',
      activatedBy: 'فعّله',
      activatedAt: 'تاريخ التفعيل',
      expiresAt: 'تاريخ الانتهاء',
      paste: 'لصق',
      clear: 'مسح',
      support: 'فتح طلب دعم',
      purchase: 'شراء ترخيص',
      history: 'عمليات التحقق الأخيرة',
      noHistory: 'ستظهر عمليات التحقق الأخيرة هنا.'
    }
  },
};
